import { createContext, useContext, useEffect, useSyncExternalStore } from 'react';

// WHICH PHASES, AND WHICH OF THEIR SUBPHASES, HAVE A BADGE SPINNING — for the phase bar's hatching
// and the subphase cards'.
//
// Each phase's measure in phaseProgress said "busy" off the stores it knew — Phase 2's off its
// list and its images, not its crops or its selection — so a phase could spin a badge for
// minutes under a number that sat still. Every call badge already knows when it runs; it says
// so here, under the phase and the subphase it is drawn in, and the bars read the union.
//
// WHERE A BADGE IS comes from where it is drawn — NoticeViewer wraps each phase, each phase
// with subphases wraps what belongs to one — not from the badge: the badges are shared by every
// phase and none of them knows which. Keyed by phase alone, as one document is open at a time
// and its phases unmount with it.
export const PhaseNumber = createContext<number | null>(null);
export const SubphaseId = createContext<string | null>(null);

// "4" for a phase, "4/substeps" for one of its subphases.
export const subphaseKey = (phase: number, sub: string) => `${phase}/${sub}`;

const counts = new Map<string, number>();
const listeners = new Set<() => void>();
let snapshot: ReadonlySet<string> = new Set();

const bump = (keys: string[], by: number) => {
  for (const key of keys) {
    const n = (counts.get(key) ?? 0) + by;
    if (n > 0) counts.set(key, n);
    else counts.delete(key);
  }
  snapshot = new Set(counts.keys());
  listeners.forEach((l) => l());
};

// Said by a badge: while `busy`, its phase — and its subphase, where it is drawn in one — counts
// as working.
export function useReportBusy(busy: boolean) {
  const phase = useContext(PhaseNumber);
  const sub = useContext(SubphaseId);
  useEffect(() => {
    if (!busy || phase == null) return;
    const keys = [String(phase), ...(sub ? [subphaseKey(phase, sub)] : [])];
    bump(keys, 1);
    return () => bump(keys, -1);
  }, [busy, phase, sub]);
}

// Read by the bars: every phase and subphase with a badge spinning right now (see subphaseKey).
export function useBusyKeys(): ReadonlySet<string> {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => snapshot,
  );
}
